/**
 * voiceFormat — le vrai format d'un enregistrement : son type MIME, sans paramètres, et l'extension
 * qui va avec.
 *
 * L'ancien enregistreur nommait tout `.m4a` et typait tout `audio/m4a`, quel que soit le fichier
 * (un WebM de Firefox compris). Le type MIME doit être le vrai, et sous sa forme de base : un type
 * avec paramètres (`audio/webm;codecs=opus`, que Chrome pose sur ses enregistrements) faisait
 * refuser le fichier par le serveur, qui compare les types à l'identique. Le serveur de la
 * messagerie, lui, devine l'extension du contenu reçu (`guessExtension`) : il accepte un MP4 comme
 * un WebM, pourvu que ce soit de l'audio.
 */

/**
 * Sur le web, `MediaRecorder` écrit du WebM/Opus par défaut dans Chromium et Firefox, que le
 * lecteur d'un iPhone ne décode pas. On prend un MP4/AAC dès que le navigateur sait l'écrire
 * (Safari toujours, Chromium 130+ presque partout) ; Firefox garde le WebM en repli. Liste reprise
 * de l'ancien enregistreur (`AudioRecordingContainer`).
 */
export const WEB_RECORDER_PREFERRED_MIME_TYPES = ["audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/aac"] as const

/** Le premier type de la liste que le navigateur sait écrire ; `null` : son défaut (WebM). */
export function pickWebRecorderMimeType(
    isTypeSupported: ((type: string) => boolean) | null | undefined
): string | null {
    if (isTypeSupported == null) return null
    for (const type of WEB_RECORDER_PREFERRED_MIME_TYPES) {
        if (isTypeSupported(type)) return type
    }
    return null
}

/** « audio/webm;codecs=opus » → « audio/webm » ; vide ou absent → `null`. */
export function baseMimeType(raw: string | null | undefined): string | null {
    const base = raw?.split(";")[0]?.trim().toLowerCase()
    return base != null && base !== "" ? base : null
}

const BY_EXTENSION: Record<string, string> = {
    m4a: "audio/mp4",
    mp4: "audio/mp4",
    aac: "audio/aac",
    webm: "audio/webm",
    ogg: "audio/ogg",
    oga: "audio/ogg",
    wav: "audio/wav",
    mp3: "audio/mpeg",
    caf: "audio/x-caf",
    "3gp": "audio/3gpp",
}

const BY_MIME: Record<string, string> = {
    "audio/mp4": ".m4a",
    "audio/x-m4a": ".m4a",
    "audio/m4a": ".m4a",
    "video/mp4": ".m4a",
    "audio/aac": ".aac",
    "audio/webm": ".webm",
    "video/webm": ".webm",
    "audio/ogg": ".ogg",
    "audio/wav": ".wav",
    "audio/x-wav": ".wav",
    "audio/mpeg": ".mp3",
    "audio/x-caf": ".caf",
    "audio/3gpp": ".3gp",
}

/** Le type que l'extension d'une adresse dit (`file:///…/recording.m4a` → `audio/mp4`) ; `null` sinon. */
export function mimeTypeFromUri(uri: string | null | undefined): string | null {
    const match = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(uri ?? "")
    return match ? (BY_EXTENSION[match[1]!.toLowerCase()] ?? null) : null
}

/** L'extension d'un type (`audio/webm` → `.webm`) ; un type inconnu prend son sous-type. */
export function extensionForMimeType(mimeType: string): string {
    const base = baseMimeType(mimeType) ?? ""
    return BY_MIME[base] ?? `.${base.split("/")[1] ?? "bin"}`
}

export type VoiceFormat = { mimeType: string; extension: string }

/** Ce qu'enregistre l'app en natif (le préréglage voix de `useVoiceRecorder` : AAC dans un MP4). */
export const NATIVE_RECORDING_FORMAT: VoiceFormat = { mimeType: "audio/mp4", extension: ".m4a" }

/**
 * Le format d'un enregistrement fini : d'abord le type que le fichier porte (le `Blob` du web),
 * puis l'extension de son adresse (le fichier du natif), sinon celui du natif.
 */
export function recordingFormat(uri: string | null | undefined, fileType?: string | null): VoiceFormat {
    const mimeType = baseMimeType(fileType) ?? mimeTypeFromUri(uri)
    return mimeType != null ? { mimeType, extension: extensionForMimeType(mimeType) } : NATIVE_RECORDING_FORMAT
}
